import { Injectable, inject } from '@angular/core';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { first, switchMap, tap } from 'rxjs/operators';
import { NIPTSignee } from '../../core.types';
import { Logger } from '../../services/logger.service';
import { SigneeService } from '../../services/signee.service';
import { ClearSigneeList, LoadSigneesAction } from './signees.actions';

const SIGNEE_STATE_NAME = 'signee';

export interface SigneeStateModel {
  signee: NIPTSignee[];
}

@State<SigneeStateModel>({
  name: SIGNEE_STATE_NAME,
  defaults: {
    signee: [],
  },
})
@Injectable()
export class SigneeState {
  private signeeService = inject(SigneeService);
  private accountUtils = inject(AccountUtilsService);

  private log = new Logger(this.constructor.name);

  @Selector()
  static getSigneeList(state: SigneeStateModel) {
    return state.signee
      .slice()
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  @Selector()
  static getSigneeById(state: SigneeStateModel) {
    return (signeeId: string) =>
      state.signee.find((signee) => signee.id === signeeId);
  }

  @Selector()
  static getSigneeListByLaboratory(state: SigneeStateModel) {
    return (laboratory: string) =>
      state.signee
        .filter(
          (signee) =>
            signee.laboratoryName.toLowerCase() === laboratory.toLowerCase()
        )
        .slice()
        .sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
  }

  @Selector()
  static getSigneeListByLaboratoryTag(state: SigneeStateModel) {
    return (laboratory: string) =>
      state.signee
        .filter(
          (signee) =>
            signee.laboratoryTag.toLowerCase() === laboratory.toLowerCase()
        )
        .slice()
        .sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
  }

  @Action(ClearSigneeList)
  clearSigneeList({ setState }: StateContext<SigneeStateModel>) {
    return setState({
      signee: [],
    });
  }
  @Action(LoadSigneesAction)
  loadSigneeAction({ patchState }: StateContext<SigneeStateModel>) {
    this.log.debug('load Signee');

    return this.accountUtils.getLaboratory$().pipe(
      switchMap((laboratory) =>
        this.signeeService.getSigneeList(
          laboratory ? { LaboratoryNames: laboratory } : {}
        )
      ),
      first(),
      tap((signeList) => {
        patchState({ signee: signeList });
      })
    );
  }
}
